<template>
  <Teleport to="body">
    <base-modal
      class="menu-item-details-modal"
      :class="{ 'menu-item-details-modal--has-image': product.imageUrl }"
      modal-class="menu-item-details-modal__modal"
      :is-visible="true"
      :modal-name="`${product.name} - ${t('iorder-view-details')}`"
      close-on-click-outside
      @close-modal="emit('close')"
    >
      <div class="menu-item-details-modal__body">
        <base-image
          v-if="product.imageUrl"
          class="menu-item-details-modal__image"
          :desktop-url="product.imageUrl"
          :desktop-alt="product.imageAltText || product.name"
          :custom-params="{ desktop: 700, tablet: 700, mobile: 715, mobileS: 430 }"
        />
        <div class="menu-item-details-modal__content">
          <div class="menu-item-details-modal__header">
            <h3 class="menu-item-details-modal__title font-m">{{ product.name }}</h3>
            <div v-if="portions.length" class="menu-item-details-modal__portions">
              <template v-if="portions.length === 1">
                <span v-if="portions[0].price" class="menu-item-details-modal__portion-price">
                  {{ portions[0].formatedPrice }}
                </span>
              </template>
              <template v-for="portion in portions" v-else :key="portion.name">
                <div v-if="portion.price" class="menu-item-details-modal__portion">
                  <div class="sr-only">{{ `${portion.name} - ${portion.formatedPrice}` }}</div>
                  <span aria-hidden="true" class="menu-item-details-modal__portion-name">
                    {{ portion.name }}
                  </span>
                  <span aria-hidden="true" class="menu-item-details-modal__portion-price">
                    {{ portion.formatedPrice }}
                  </span>
                </div>
              </template>
            </div>
          </div>
          <div v-if="modalTags.length" class="menu-item-details-modal__tags">
            <template v-for="{ name, green, icon, label } in modalTags" :key="name">
              <base-badge :variant="green ? 'filled' : 'outline'" :icon="icon">
                {{ label || name }}
              </base-badge>
            </template>
          </div>
          <p v-if="description" class="menu-item-details-modal__description">
            {{ description }}
          </p>
          <section v-if="allergensLinkHref" class="menu-item-details-modal__allergens">
            <p class="menu-item-details-modal__allergy-disclaimer">
              {{ t('iorder-allergy-disclaimer') }}
            </p>
            <base-link
              v-if="allergensLinkHref"
              class="menu-item-details-modal__allergens-link"
              :field="{
                value: {
                  href: allergensLinkHref,
                  text: t('iorder-allergens-nutrition'),
                  target: '_blank',
                },
              }"
              @click="onAllergenLinkClick({ href: allergensLinkHref, dl_menuSection: 'allergens' })"
            />
          </section>

          <section
            v-for="prompt in prompts"
            :key="prompt.id"
            class="menu-item-details-modal__prompt"
          >
            <h4 class="menu-item-details-modal__prompt-heading">{{ prompt.text }}</h4>
            <ul class="menu-item-details-modal__choices">
              <li
                v-for="choice in prompt.choices"
                :key="choice.id"
                class="menu-item-details-modal__choice"
                :class="{ 'menu-item-details-modal__choice--unavailable': choice.outOfStock }"
                :aria-disabled="choice.outOfStock || undefined"
              >
                <div class="menu-item-details-modal__choice-row">
                  <span class="menu-item-details-modal__choice-name">{{ choice.name }}</span>
                  <span
                    v-if="choice.outOfStock"
                    class="menu-item-details-modal__choice-unavailable"
                    role="note"
                  >
                    {{ t('iorder-unavailable') }}
                  </span>
                  <span
                    v-else-if="choicePrice(choice)"
                    class="menu-item-details-modal__choice-price"
                  >
                    +{{ choicePrice(choice) }}
                  </span>
                </div>
                <div class="menu-item-details-modal__choice-meta">
                  <base-badge
                    v-if="choiceCalories(choice)"
                    class="menu-item-details-modal__choice-calories"
                  >
                    {{ choiceCalories(choice) }} kcal
                  </base-badge>
                  <base-badge
                    v-for="tag in choiceTags(choice)"
                    :key="tag.name"
                    class="menu-item-details-modal__choice-keyword"
                    :variant="tag.green ? 'filled' : 'outline'"
                    :icon="tag.icon"
                  >
                    {{ tag.label || tag.name }}
                  </base-badge>
                </div>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </base-modal>
  </Teleport>
</template>

<script setup lang="ts">
import type { MenuItemDetailsModalProps } from './types';
import type { MenuPortion, MenuProduct, MenuProductOption, MenuPromptChoice } from '@/types/menu';
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { twoDecimalsFilter } from '../../../utils/filters';
import BaseModal from '../../2_elements/BaseModal/BaseModal.vue';
import BaseImage from '../../2_elements/BaseImage/BaseImage.vue';
import BaseLink from '../../2_elements/BaseLink/BaseLink.vue';
import BaseBadge from '../../2_elements/BaseBadge/BaseBadge.vue';
import { useMenuInteractionTracking } from '../../../data-layer/hooks/useMenuInteractionTracking.js';
import { tagsData } from '../MenuItem/tagsData.js';

const { trackMenuInteraction } = useMenuInteractionTracking();
const { t } = useI18n();

const allergenData = inject('allergenData', ref<{ allergensLink?: { href?: string } }>({}));

const props = withDefaults(defineProps<MenuItemDetailsModalProps>(), {
  product: () => ({}) as MenuProduct,
  tags: () => [],
});

const emit = defineEmits<{ close: [] }>();

const removeCalories = (str: string) =>
  str ? str.replaceAll(/\s*\([^)]*kcal[^)]*\)/gi, '').trim() : '';

const description = computed(() => removeCalories(props.product?.description || ''));

const modalTags = computed(() => props.tags?.filter((tag) => !tag.allergenLink) ?? []);

const allergensLinkHref = computed(() => allergenData?.value?.allergensLink?.href || '');

const portions = computed(() =>
  props.product?.portions
    ? props.product.portions
        .map((portion: MenuPortion) => ({
          ...portion,
          formatedPrice: `£${twoDecimalsFilter(portion.price)}`,
        }))
        .sort((a: MenuPortion, b: MenuPortion) => a.price - b.price)
    : []
);

const prompts = computed<MenuProductOption[]>(() =>
  (props.product?.options || []).filter(
    (item): item is MenuProductOption => item?.type === 'PROMPT'
  )
);

const choicePortion = (choice: MenuPromptChoice) => choice?.portions?.[0];

const choiceCalories = (choice: MenuPromptChoice) => {
  const calories = choicePortion(choice)?.calories;
  return calories && calories > 0 ? calories : null;
};

const choiceTags = (choice: MenuPromptChoice) =>
  choice?.keywords?.length ? tagsData.filter((tag) => choice.keywords!.includes(tag.name)) : [];

const choicePrice = (choice: MenuPromptChoice) => {
  const portion = choicePortion(choice);
  const price = portion?.supplementPrice ?? portion?.price;
  return price && price > 0 ? `£${twoDecimalsFilter(price)}` : null;
};

const onAllergenLinkClick = ({
  href,
  dl_menuSection = '',
}: {
  href: string;
  dl_menuSection?: string;
}) => {
  trackMenuInteraction(href, dl_menuSection, 'download');
};
</script>

<style lang="scss">
@import './scss/MenuItemDetailsModal.scss';
</style>
